import {
  Box, IconButton, Popover, Stack, Typography,
} from '@mui/material';
import React from 'react';

import Grid from '@mui/material/Grid'; // Grid version 1
// Grid version 2
import { AddCircle, Circle } from '@mui/icons-material';
import { BrushType } from 'pages/sessions/components/digital-coaching-tool/interface';
import {
  BrushIcon,
  ColorSwatchIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  LampOnIcon,
  PathIcon, RotateLeftIcon, TrashFillIcon,
} from '../../../../../../assets/icons';
import { PrimaryTextField } from '../../../../../../components';

interface Props {
  brushTypes: BrushType[];
  onBrushSelect: (brushType: BrushType) => void;
  selectedBrushType: BrushType;
  brushSizes: number[];
  selectedBrushSize: number;
  onBrushSizeChange: (brushSize: number) => void;
  highlight: boolean;
  onToggleHighlight: (value: boolean) => void;
  brushColors: string[];
  selectedBrushColor: string;
  setBrushColors: (color: string) => void;
  onColorSelect: (color: string) => void;
  onHistoryAllClear: () => void;
  onHistoryRedo: () => void;
}

const Tools = ({
  brushTypes,
  selectedBrushType,
  onBrushSelect,
  brushSizes,
  selectedBrushSize,
  onBrushSizeChange,
  highlight,
  onToggleHighlight,
  brushColors,
  selectedBrushColor,
  setBrushColors,
  onColorSelect,
  onHistoryAllClear,
  onHistoryRedo,
}: Props) => {
  const containerRef = React.useRef(null);
  const [showTools, setShowTools] = React.useState<boolean>(true);

  const [brushAnchorEl, setBrushAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [brushSizeAnchorEl, setBrushSizeAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [brushColorAnchorEl, setBrushColorAnchorEl] = React.useState<HTMLDivElement | null>(null);
  // const [splitAnchorElement, setSplitAnchorElement] = React.useState<HTMLDivElement | null>(
  //   null,
  // );
  const [newColor, setNewColor] = React.useState<string>('');

  const openBrush = (event: any) => {
    setBrushAnchorEl(event.currentTarget);
    onToggleHighlight(false);
  };

  const openBrushSize = (event: any) => {
    setBrushSizeAnchorEl(event.currentTarget);
    onToggleHighlight(false);
  };

  const openBrushColor = (event: any) => {
    setBrushColorAnchorEl(event.currentTarget);
    onToggleHighlight(false);
  };

  // const openSplit = (event: any) => {
  //   setSplitAnchorElement(event.currentTarget);
  //   onToggleHighlight(false);
  // };

  const closeBrush = () => {
    setBrushAnchorEl(null);
  };

  const closeBrushSize = () => {
    setBrushSizeAnchorEl(null);
  };

  const closeBrushColor = () => {
    setBrushColorAnchorEl(null);
  };

  // const closeSplit = () => {
  //   setSplitAnchorElement(null);
  // };

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewColor(event.target.value);
  };

  const isBrushOpen = Boolean(brushAnchorEl);
  const isBrushSizeOpen = Boolean(brushSizeAnchorEl);
  const isBrushColorOpen = Boolean(brushColorAnchorEl);
  // const isSplitOpen = Boolean(splitAnchorElement);

  return (
    <div className="tools-container d-flex align-items-center" ref={containerRef}>
      <Box
        sx={{
          py: 1.5,
          px: 1,
          border: 1,
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
          borderColor: 'divider',
          lineHeight: 1,
          cursor: 'pointer',
          backgroundColor: 'background.paper',
        }}
        onClick={() => setShowTools(!showTools)}
      >
        { showTools ? (
          <DoubleArrowRightIcon sx={{ color: 'primary.dark', fontSize: 12 }} />
        ) : (
          <DoubleArrowLeftIcon sx={{ color: 'primary.dark', fontSize: 12 }} />
        )}
      </Box>
      <Stack
        sx={{
          p: 1,
          border: 1,
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
          borderColor: 'divider',
          lineHeight: 1,
          backgroundColor: 'background.paper',

        }}
        className={`${!showTools ? 'collapse' : 'expand'}`}
      >

        <Box className="p-2">
          <IconButton onClick={() => { onHistoryRedo(); }} sx={{ color: 'primary.dark', p: 0 }}>
            <RotateLeftIcon sx={{ cursor: 'pointer' }} />
          </IconButton>
        </Box>
        <Box className="p-2">
          <IconButton onClick={() => { onHistoryAllClear(); }} sx={{ color: 'primary.dark', p: 0 }}>
            <TrashFillIcon sx={{ cursor: 'pointer' }} />
          </IconButton>
        </Box>
        <Box className="p-2">
          <IconButton onClick={() => { onToggleHighlight(!highlight); }} sx={{ color: highlight ? 'primary.main' : 'primary.dark', p: 0 }}>
            <LampOnIcon sx={{ cursor: 'pointer' }} />
          </IconButton>
        </Box>
        <Box className="p-2" onClick={openBrush}>
          <PathIcon sx={{ color: highlight ? 'primary.dark' : 'primary.main', cursor: 'pointer' }} />
        </Box>
        <Box className="p-2" onClick={openBrushSize}>
          <BrushIcon sx={{ color: 'primary.dark', cursor: 'pointer' }} />
        </Box>
        <Box className="p-2" onClick={openBrushColor}>
          <ColorSwatchIcon sx={{ color: selectedBrushColor, cursor: 'pointer' }} />
        </Box>
        {/* <Box className="p-2">
          <SplitScannerIcon sx={{ color: 'primary.dark', cursor: 'pointer' }} />
        </Box> */}

        <Popover
          container={containerRef.current}
          open={isBrushOpen}
          anchorEl={brushAnchorEl}
          onClose={closeBrush}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <div
            className="py-3 px-4"
            style={{ maxWidth: 300, backgroundColor: 'background.paper' }}
          >
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              Brush types
            </Typography>

            <Grid container spacing={1}>

              {
                brushTypes.map((item, index) => (
                  <Grid
                    key={item.name}
                    sx={{
                      width: 70, height: 70, maxWidth: 70, maxHeight: 70,
                    }}
                    xs={4}
                    className="d-flex flex-column justify-content-center align-items-center"
                  >
                    <IconButton
                      className={`d-flex flex-column justify-content-center align-items-center ${index < brushTypes.length - 2 && 'mb-1'}`}
                      onClick={() => {
                        onBrushSelect(item);
                        closeBrush();
                      }}
                      sx={{ color: selectedBrushType.name === item.name ? 'primary.main' : 'primary.dark' }}
                    >
                      {item.icon}
                      <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1, mt: 1 }}>
                        {item.title}
                      </Typography>
                    </IconButton>
                  </Grid>
                ))
              }

            </Grid>

          </div>
        </Popover>

        <Popover
          container={containerRef.current}
          open={isBrushSizeOpen}
          anchorEl={brushSizeAnchorEl}
          onClose={closeBrushSize}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <div
            className="py-3 px-4"
            style={{ maxWidth: 300, backgroundColor: 'background.paper' }}
          >
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              Brush Size
            </Typography>

            <Stack alignItems="center" direction="row" spacing={1.0}>
              {brushSizes.map((brushSize) => (
                <div className="col-auto" key={brushSize}>
                  <IconButton
                    className="d-flex flex-column justify-content-center align-items-center"
                    onClick={() => {
                      onBrushSizeChange(brushSize);
                      closeBrushSize();
                    }}
                    sx={{ color: selectedBrushSize === brushSize ? 'primary.main' : 'primary.dark' }}
                  >
                    <Circle sx={{ fontSize: brushSize * 1.5 }} />
                  </IconButton>
                </div>
              ))}
            </Stack>

          </div>
        </Popover>

        <Popover
          container={containerRef.current}
          open={isBrushColorOpen}
          anchorEl={brushColorAnchorEl}
          onClose={closeBrushColor}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <div
            className="py-3 px-4"
            style={{ maxWidth: 224, backgroundColor: 'background.paper', overflow: 'hidden' }}
          >
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              Brush Color
            </Typography>

            <PrimaryTextField sx={{ mt: 0, mb: 1.8 }} fullWidth placeholder="#000000" onChange={handleColorChange} />

            <Box className="row gx-2">
              {brushColors.map((brushColor) => (
                <div className="col-auto" key={brushColor}>
                  <IconButton
                    sx={{ p: 0, border: selectedBrushColor === brushColor ? '1px solid #3030f9' : '1px solid #fff' }}
                    onClick={() => {
                      onColorSelect(brushColor);
                      closeBrushColor();
                    }}
                  >
                    <Circle sx={{ color: brushColor, fontSize: 28.8 }} />
                  </IconButton>
                </div>
              ))}
              <div className="col-auto">
                <IconButton
                  sx={{ p: 0 }}
                  onClick={() => {
                    if (newColor && (newColor.length === 4 || newColor.length === 7)) {
                      setBrushColors(newColor);
                      onColorSelect(newColor);
                      closeBrushColor();
                    }
                  }}
                >
                  <AddCircle sx={{ color: 'primary.dark', fontSize: 28.8 }} />
                </IconButton>
              </div>

            </Box>

          </div>
        </Popover>

      </Stack>
    </div>
  );
};

export default Tools;
